import React, { useState, useEffect, useRef, useCallback } from 'react';
import UIDock from './UIDock';
import MiniAvatar from './MiniAvatar';
import AIUpgradeModal from './AIUpgradeModal';
import { useSpeechToText } from './useSpeechToText';
import { useAIIntent } from './useAIIntent';
import { useTextToSpeech } from './useTextToSpeech';
import { handleAIIntent } from './intentController';
import { AIIntent } from './types';
import { aiUsageService, AIUsageInfo } from '../../services/aiUsageService';
import { auth } from '../../firebase';
import { useNavigation } from '../AIAssistantV2/hooks/useNavigation';

// Image/video assets
// eslint-disable-next-line @typescript-eslint/no-var-requires
const idleImg = require('./Video_vorm/Idle_state_afbeelding.jpeg');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const listeningVideo = require('./Video_vorm/Listening_states_1.mp4');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const speakingVideo = require('./Video_vorm/speaking.webm');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const introVideo = require('./Video_vorm/intro_states.mp4');

// ============================================================================
// TYPES
// ============================================================================

type AvatarState = 'idle' | 'listening' | 'speaking';
type AssistantPhase = 'off' | 'intro' | 'idle' | 'listening' | 'processing' | 'speaking';

interface AIAssistantOverlayProps {
  onNavigate?: (target: string) => void;
  onClose?: () => void;
  userName?: string;
}

const StateLogo: React.FC<{ phase: AssistantPhase; onIntroEnd?: () => void }> = ({ phase, onIntroEnd }) => {
  const introVideoRef = React.useRef<HTMLVideoElement>(null);
  const speakingVideoRef = React.useRef<HTMLVideoElement>(null);
  const listeningVideoRef = React.useRef<HTMLVideoElement>(null);
  
  const handleIntroEnd = React.useCallback(() => {
    console.log('🎥 Intro video onEnded event fired!');
    if (onIntroEnd) {
      onIntroEnd();
    }
  }, [onIntroEnd]);
  
  const handleIntroLoadedData = React.useCallback(() => {
    console.log('📹 Intro video loaded, duration:', introVideoRef.current?.duration);
  }, []);
  
  const handleIntroPlay = React.useCallback(() => {
    console.log('▶️ Intro video started playing');
  }, []);
  
  // Start playing videos when they become visible
  React.useEffect(() => {
    if (phase === 'speaking' && speakingVideoRef.current) {
      speakingVideoRef.current.play().catch(() => {});
    }
    if (phase === 'listening' && listeningVideoRef.current) {
      listeningVideoRef.current.play().catch(() => {});
    }
  }, [phase]);
  
  // Layered rendering: all media always rendered, controlled by opacity
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Layer 0: Idle image (base layer, always visible when nothing else is) */}
      <img
        src={idleImg}
        alt="idle"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: phase === 'idle' || phase === 'processing' ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          zIndex: 0
        }}
      />
      
      {/* Layer 1: Listening video */}
      <video
        ref={listeningVideoRef}
        src={listeningVideo}
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: phase === 'listening' ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          zIndex: 1
        }}
      />
      
      {/* Layer 2: Speaking video */}
      <video
        ref={speakingVideoRef}
        src={speakingVideo}
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: phase === 'speaking' ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          zIndex: 2
        }}
      />
      
      {/* Layer 3: Intro video (highest priority during intro) */}
      <video
        ref={introVideoRef}
        src={introVideo}
        muted
        playsInline
        autoPlay
        onEnded={handleIntroEnd}
        onLoadedData={handleIntroLoadedData}
        onPlay={handleIntroPlay}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: phase === 'intro' ? 1 : 0,
          transition: 'opacity 1s ease-in-out',
          zIndex: 3
        }}
      />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AIAssistantOverlay: React.FC<AIAssistantOverlayProps> = ({ onNavigate, onClose, userName }) => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  // Core assistant state - single source of truth
  const [phase, setPhase] = useState<AssistantPhase>('idle');
  
  // UI state
  const [isVisible, setIsVisible] = useState(false); // Start gesloten, alleen mini avatar zichtbaar
  const [isEnabled, setIsEnabled] = useState(() => {
    try {
      return localStorage.getItem('ai_assistant_enabled') !== 'false';
    } catch {
      return true;
    }
  });
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('ai_assistant_muted') === 'true';
    } catch {
      return false;
    }
  });
  const [hasGreeted, setHasGreeted] = useState(false);
  const [hasPlayedIntro, setHasPlayedIntro] = useState(false);
  const [hasAutoListened, setHasAutoListened] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [browserWarning, setBrowserWarning] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Usage tracking
  const [usageInfo, setUsageInfo] = useState<AIUsageInfo | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);

  // Navigation helper (shared with V2)
  const { navigate } = useNavigation({ onNavigate });


  // Hooks
  const { 
    transcript, 
    interimTranscript, 
    startRecognition, 
    stopRecognition, 
    clearTranscript, 
    isSupported,
    isRecognizing,
    ensureRecognitionRunning
  } = useSpeechToText();
  
  const { intent, isLoading: isAILoading, getIntent, clearIntent } = useAIIntent();
  const { speak, stop: stopSpeech, isSpeaking } = useTextToSpeech();

  // Refs for preventing race conditions
  const processedIntentRef = useRef<AIIntent | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const noAudioTimerRef = useRef<NodeJS.Timeout | null>(null); // For listening → idle transition
  const speakingFadeTimerRef = useRef<NodeJS.Timeout | null>(null); // For speaking → idle fade-out timer
  const lastTranscriptRef = useRef<string>('');
  const hasShownIntroOnLoginRef = useRef<boolean>(false); // Track if intro was shown on login
  const isProcessingRef = useRef(false);
  const isSpeakingRef = useRef(false); // Ref mirror of isSpeaking for use inside closures/animation frames
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const skipGreetingRef = useRef<boolean>(false);
  const hasCompletedIntroRef = useRef<boolean>(false); // Track if intro has been completed (prevents duplicate greetings)
  
  const [isAudioDetected, setIsAudioDetected] = useState(false);

  // ============================================================================
  // BODY SCROLL LOCK WHEN OVERLAY IS OPEN
  // ============================================================================
  
  useEffect(() => {
    if (isVisible) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isVisible]);

  // ============================================================================
  // DERIVED STATE
  // ============================================================================
  
  // Convert phase to avatar state for visual display
  const avatarState: AvatarState = 
    phase === 'speaking' ? 'speaking' :
    phase === 'listening' ? 'listening' :
    'idle';

  // Is the microphone actively listening?
  const isListening = phase === 'listening';

  // ============================================================================
  // CLEANUP HELPERS
  // ============================================================================

  const cleanupMic = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (noAudioTimerRef.current) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setIsAudioDetected(false);
    stopRecognition();
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
  }, [stopRecognition]);

  const cleanupSpeech = useCallback(() => {
    stopSpeech();
  }, [stopSpeech]);

  const fullCleanup = useCallback(() => {
    cleanupMic();
    cleanupSpeech();
    clearTranscript();
    clearIntent();
    isProcessingRef.current = false;
    // Cancel the post-speech fade-out timer so it can't fire on a closed overlay
    if (speakingFadeTimerRef.current) {
      clearTimeout(speakingFadeTimerRef.current);
      speakingFadeTimerRef.current = null;
    }
  }, [cleanupMic, cleanupSpeech, clearTranscript, clearIntent]);

  // ============================================================================
  // PHASE TRANSITIONS
  // ============================================================================

  const transitionToIdle = useCallback(() => {
    console.log('🔄 Phase → IDLE (keeping mic on)');
    
    // ============================================
    // 1. EERST: Reset alle refs (VOORDAT setPhase)
    // ============================================
    isProcessingRef.current = false;
    lastTranscriptRef.current = '';
    processedIntentRef.current = null;
    console.log('✅ REFS RESET - isProcessing:', isProcessingRef.current, 'lastTranscript:', lastTranscriptRef.current);
    
    // ============================================
    // 2. DAN: Clear timers
    // ============================================
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (noAudioTimerRef.current) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    
    // ============================================
    // 3. DAN: Clear state (speech, intent)
    // ============================================
    cleanupSpeech();
    clearIntent();
    
    // Clear display message after a delay
    setTimeout(() => {
      setDisplayMessage('');
    }, 2000);
    
    // ============================================
    // 4. LAATSTE: setPhase('idle')
    // ============================================
    setPhase('idle');
    
    // ❌ REMOVED: Don't call ensureRecognitionRunning here
    // This was causing the infinite restart loop
    
    console.log('✅ Ready for next question');
  }, [cleanupSpeech, clearIntent]);

  const startMicAndAudioDetection = useCallback(async () => {
    if (micStreamRef.current && audioContextRef.current) {
      // Ensure speech recognition is running
      if (!isRecognizing) {
        startRecognition();
      }
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      startRecognition();
      
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const detectAudio = () => {
        if (!analyserRef.current) return;
        
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
        
        // Make mic more sensitive: lower threshold and add a tiny smoothing
        const threshold = 12; // was 30
        const smoothedAverage = average * 0.9 + (isAudioDetected ? 3 : 0); // favor recent detection a bit
        setIsAudioDetected(smoothedAverage > threshold);
        
        animationFrameRef.current = requestAnimationFrame(detectAudio);
      };
      
      detectAudio();
    } catch (error) {
      console.error('❌ Mic error:', error);
    }
  }, [startRecognition, isRecognizing]);

  const transitionToListening = useCallback(async () => {
    // ✅ Check AI usage limit before starting conversation
    if (currentUser) {
      const usageCheck = await aiUsageService.checkAIUsage(currentUser.uid);
      
      if (!usageCheck.canUseAI) {
        // Show upgrade modal
        setShowUpgradeModal(true);
        return; // Don't start listening
      }
      
      // Track this conversation (only when speech is detected and processed)
      // We'll track it after first intent is processed
    }
    
    if (phase === 'speaking') {
      cleanupSpeech();
    }
    
    // Clear the no-audio timer (we're starting fresh in listening mode)
    if (noAudioTimerRef.current) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    
    clearTranscript();
    lastTranscriptRef.current = '';
    processedIntentRef.current = null;
    isProcessingRef.current = false;
    
    setPhase('listening');
    await startMicAndAudioDetection();
    
    // ❌ REMOVED: Don't call ensureRecognitionRunning here
    // startMicAndAudioDetection already starts recognition
  }, [phase, cleanupSpeech, clearTranscript, startMicAndAudioDetection, currentUser]);

  const transitionToProcessing = useCallback(() => {
    // Clear all timers
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (noAudioTimerRef.current) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    
    // Clear transcript immediately so it doesn't keep building up
    // We already saved it in lastTranscriptRef
    clearTranscript();
    setPhase('processing');
  }, [clearTranscript]);

  const transitionToSpeaking = useCallback(() => {
    setPhase('speaking');
  }, []);

  // ============================================================================
  // CLICK HANDLER - TOGGLE LISTENING/STOP
  // ============================================================================

  const handleAvatarClick = useCallback(() => {
    if (phase === 'listening' || phase === 'speaking' || phase === 'processing') {
      transitionToIdle();
    } else {
      transitionToListening();
    }
  }, [phase, transitionToIdle, transitionToListening]);

  const handleMicToggle = useCallback(() => {
    handleAvatarClick();
  }, [handleAvatarClick]);

  // ============================================================================
  // AUTO-SYNC SPEAKING STATE WITH TTS
  // ============================================================================

  // Keep isSpeakingRef in sync so animation-frame callbacks and timer closures
  // always see the current value without capturing stale state.
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  useEffect(() => {
    // Automatically transition to speaking phase when TTS actually starts
    if (isSpeaking && phase !== 'speaking') {
      console.log('🗣️ TTS started, setting phase to speaking');
      // Cancel any pending fade-out timer so a rapid stop→start doesn't ghost-idle
      if (speakingFadeTimerRef.current) {
        clearTimeout(speakingFadeTimerRef.current);
        speakingFadeTimerRef.current = null;
      }
      setPhase('speaking');
    }
    // When TTS stops and we're in speaking phase, go to idle after fade-out.
    // Use a ref-based timer so we can cancel it if TTS restarts, and read
    // live state via refs instead of stale closure values.
    else if (!isSpeaking && phase === 'speaking') {
      console.log('🔇 TTS stopped, transitioning to idle in 1.8s (post-speech buffer)');
      if (speakingFadeTimerRef.current) {
        clearTimeout(speakingFadeTimerRef.current);
      }
      speakingFadeTimerRef.current = setTimeout(() => {
        speakingFadeTimerRef.current = null;
        // Re-check via refs — isSpeaking may have restarted during the buffer window
        if (!isSpeakingRef.current) {
          transitionToIdle();
        }
      }, 1800); // 1.8s: video fade-out + enough room for speaker echo to die down
    }

    return () => {
      if (speakingFadeTimerRef.current) {
        clearTimeout(speakingFadeTimerRef.current);
        speakingFadeTimerRef.current = null;
      }
    };
  }, [isSpeaking, phase, transitionToIdle]);

  useEffect(() => {
    // ❌ NEVER during intro or when muted
    if (phase === 'intro' || !hasPlayedIntro || isMuted) {
      return;
    }
    
    // Auto-transitions: luister naar audio in alle modes (fullscreen + compact)
    if (phase !== 'listening' && phase !== 'idle') {
      // Clean up timer if we're in a different phase
      if (noAudioTimerRef.current) {
        clearTimeout(noAudioTimerRef.current);
        noAudioTimerRef.current = null;
      }
      return;
    }

    // 🔇 NEVER react to audio while the AI itself is speaking — the microphone
    // would otherwise pick up the AI's own voice from the speakers, creating a
    // feedback loop where the AI endlessly responds to its own output.
    if (isSpeaking) {
      return;
    }

    if (isAudioDetected) {
      // Clear the no-audio timer when audio is detected
      if (noAudioTimerRef.current) {
        clearTimeout(noAudioTimerRef.current);
        noAudioTimerRef.current = null;
      }
      
      // Transition from idle to listening when audio is detected
      if (phase === 'idle') {
        setPhase('listening');
        // Ensure mic and speech recognition are running
        startMicAndAudioDetection();
        // ❌ REMOVED: Don't call ensureRecognitionRunning
      }
    } else {
      // No audio detected
      if (phase === 'listening' && !isProcessingRef.current && !silenceTimerRef.current) {
        // Only start timer if we don't already have one
        if (!noAudioTimerRef.current) {
          noAudioTimerRef.current = setTimeout(() => {
            if (phase === 'listening' && !isAudioDetected && !isProcessingRef.current && !silenceTimerRef.current) {
              transitionToIdle();
            }
            noAudioTimerRef.current = null;
          }, 2000);
        }
      }
    }

    return () => {
      // Cleanup on unmount
      if (noAudioTimerRef.current) {
        clearTimeout(noAudioTimerRef.current);
        noAudioTimerRef.current = null;
      }
    };
  }, [isAudioDetected, isSpeaking, phase, hasPlayedIntro, startMicAndAudioDetection, ensureRecognitionRunning, transitionToIdle]);

  // ============================================================================
  // AUTO-PROCESS TRANSCRIPT AFTER SILENCE
  // ============================================================================

  useEffect(() => {
    // ❌ NEVER during intro or when muted
    if (phase === 'intro' || !hasPlayedIntro || isMuted) {
      return;
    }
    
    // Auto-process transcript in alle modes (fullscreen + compact)
    // Process when in listening or idle phase (mic is always on)
    if (phase !== 'listening' && phase !== 'idle') {
      console.log('❌ Skipping processing - wrong phase:', phase);
      return;
    }
    
    const trimmedTranscript = transcript.trim();
    
    // No transcript yet
    if (!trimmedTranscript) return;
    
    // Already processing this transcript
    if (trimmedTranscript === lastTranscriptRef.current) {
      console.log('⏭️ Skipping - already processed:', trimmedTranscript.substring(0, 30));
      return;
    }
    
    // Already processing something else
    if (isProcessingRef.current) {
      console.log('⏭️ Skipping - already processing');
      return;
    }
    
    // Clear previous timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    
    console.log('📝', trimmedTranscript.substring(0, 50));
    
    // Check if it's a navigation or action command (instant processing)
    const isActionCommand = /\b(ga naar|open|navigeer|toon|laat|bekijk|bel|plan|pauze|stop)\b/i.test(trimmedTranscript);
    const silenceDelay = isActionCommand ? 150 : 250; // ⚡ ultrasnel: 0.15s / 0.25s
    
    // Start silence timer - process after silence
    silenceTimerRef.current = setTimeout(() => {
      // Double-check we're not already processing
      if (isProcessingRef.current) return;

      const lower = trimmedTranscript.toLowerCase();

      // Quick commands (bypass LLM) for agenda buttons
      const handleQuickCommand = (): boolean => {
        // Vrij voor netwerken
        if (
          lower.includes('vrij voor netwerken') ||
          lower.includes('networking') ||
          lower.includes('netwerkblok')
        ) {
          console.log('⚡ Quick command: open_agenda_networking');
          isProcessingRef.current = true;
          navigate('agenda');
          setIsVisible(false);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('openAgendaNetworking'));
            isProcessingRef.current = false;
            transitionToIdle();
          }, 120);
          return true;
        }

        // Nieuw agendapunt
        if (
          lower.includes('nieuw agendapunt') ||
          lower.includes('nieuwe afspraak') ||
          lower.includes('nieuw event') ||
          lower.includes('nieuwe afspraak maken')
        ) {
          console.log('⚡ Quick command: open_agenda_new_item');
          isProcessingRef.current = true;
          navigate('agenda');
          setIsVisible(false);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('openNewAgendaItem'));
            isProcessingRef.current = false;
            transitionToIdle();
          }, 120);
          return true;
        }

        return false;
      };

      if (handleQuickCommand()) return;
      
      console.log('⚡ Processing:', trimmedTranscript.substring(0, 40));
      
      // Mark as processing to prevent duplicates
      isProcessingRef.current = true;
      lastTranscriptRef.current = trimmedTranscript;
      
      // Transition to processing
      transitionToProcessing();
      
      // Get AI intent (character tracking happens after AI response)
      getIntent(trimmedTranscript);
      
    }, silenceDelay);
    
    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, [transcript, phase, hasPlayedIntro, getIntent, transitionToProcessing, navigate, transitionToIdle]);

  // ============================================================================
  // HANDLE AI INTENT RESPONSE
  // ============================================================================

  useEffect(() => {
    // Only handle new intents during processing phase OR if we somehow missed it
    if (phase !== 'processing' && phase !== 'listening' && phase !== 'idle') return;
    if (!intent) return;
    if (isAILoading) return;
    if (intent === processedIntentRef.current) return;
    
    console.log('🎯', intent.type);
    processedIntentRef.current = intent;
    
    // Ensure we're in processing phase
    if (phase !== 'processing') {
      transitionToProcessing();
    }
    
    const execution = handleAIIntent(intent);
    
    // Handle the action
    const executeAction = async () => {
      // 1. Handle navigation actions (silent)
      if (execution.action === 'navigate' && execution.actionData?.target) {
        const target = execution.actionData.target;
        setIsNavigating(true);
        navigate(target);
        setIsVisible(false); // sluit overlay zodat pagina zichtbaar is
        setIsNavigating(false);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 2. Handle open_newsfeed
      if (execution.action === 'open_newsfeed') {
        
        // First navigate to dashboard if not already there
        navigate('dashboard');
        setIsVisible(false);

        try {
          sessionStorage.setItem('pendingOpenNewsfeed', '1');
        } catch {}
        
        // Then open the expanded newsfeed modal
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openNewsfeed'));
        }, 100);
        
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 3. Handle open_inbox_tab
      if (execution.action === 'open_inbox_tab' && execution.actionData?.tab) {
        navigate('inbox');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openInboxTab', { detail: { tab: execution.actionData.tab } }));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 4. Handle open_connectieflow
      if (execution.action === 'open_connectieflow') {
        navigate('connecties');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openConnectionFlow'));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 4b. Handle agenda quick actions
      if (execution.action === ('open_agenda_networking' as any)) {
        navigate('agenda');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openAgendaNetworking'));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }

      if (execution.action === ('open_agenda_new_item' as any)) {
        navigate('agenda');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openNewAgendaItem'));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 5. Handle show_updates (speaks)
      if (execution.action === 'show_updates') {
        // TODO: Get real data from Firebase
        const updatesSummary = `Je hebt 3 ongelezen berichten, 2 vergaderingen vandaag, en 1 nieuwe connectie verzoek. Waar wil je mee beginnen?`;
        
        setDisplayMessage(updatesSummary);
        
        // Speaking phase will be triggered automatically by isSpeaking state
        await speak(updatesSummary);
        
        // After speaking, listening will be triggered automatically
        return;
      }

      // 5b. Handle Agenda quick actions
      if (execution.action === 'open_agenda_networking') {
        navigate('agenda');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openAgendaNetworking'));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }

      if (execution.action === 'open_agenda_new_item') {
        navigate('agenda');
        setIsVisible(false);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openNewAgendaItem'));
        }, 100);
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 6. Handle ConnectieFlow call
      if (execution.action === 'connectieflow_call') {
        window.dispatchEvent(new CustomEvent('connectieflowCall', { 
          detail: execution.actionData 
        }));
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 7. Handle ConnectieFlow schedule
      if (execution.action === 'connectieflow_schedule') {
        window.dispatchEvent(new CustomEvent('connectieflowSchedule', { 
          detail: execution.actionData 
        }));
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 8. Handle ConnectieFlow pause
      if (execution.action === 'connectieflow_pause') {
        window.dispatchEvent(new CustomEvent('connectieflowPause', { 
          detail: execution.actionData 
        }));
        setTimeout(() => transitionToIdle(), 150);
        return;
      }
      
      // 9. Handle speech responses
      if (execution.shouldSpeak && execution.speechText) {
        setDisplayMessage(execution.speechText);
        
        // Track character usage (user input + AI response)
        if (currentUser && lastTranscriptRef.current) {
          const userCharacters = lastTranscriptRef.current.length;
          const aiCharacters = execution.speechText.length;
          const totalCharacters = userCharacters + aiCharacters;
          
          console.log(`📊 Character usage - User: ${userCharacters}, AI: ${aiCharacters}, Total: ${totalCharacters}`);
          
          // Track usage and refresh info
          aiUsageService.trackCharacterUsage(currentUser.uid, totalCharacters).then(() => {
            aiUsageService.getAIUsageInfo(currentUser.uid).then(setUsageInfo);
          });
        }
        
        // Speaking phase will be triggered automatically by isSpeaking state
        await speak(execution.speechText);
        
        // After speaking, listening will be triggered automatically
        return;
      }
      
      // 10. No action needed, go back to idle (microfoon blijft aan)
      transitionToIdle();
    };
    
    executeAction();
    
  }, [intent, isAILoading, phase, speak, onNavigate, transitionToProcessing]);

  // ============================================================================
  // AUTH STATE LISTENER
  // ============================================================================

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: any) => {
      const prevUser = currentUser;
      setCurrentUser(user);
      
      // Bij login: open overlay met intro (alleen bij eerste login, niet bij remount)
      if (user && !prevUser && isEnabled && !hasShownIntroOnLoginRef.current) {
        hasShownIntroOnLoginRef.current = true;
        setIsVisible(true);
        setPhase('intro');
        setHasGreeted(false);
        setHasPlayedIntro(false);
        setHasAutoListened(false);
      }
    });
    return () => unsubscribe();
  }, [isEnabled, currentUser]);

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  useEffect(() => {
    let enabled = true;
    try {
      enabled = localStorage.getItem('ai_assistant_enabled') !== 'false';
    } catch {
      enabled = true;
    }
    setIsEnabled(enabled);
    
    // Check of user al ingelogd is bij mount
    const user = auth.currentUser;
    if (user && enabled && !hasShownIntroOnLoginRef.current) {
      // User is al ingelogd bij mount - open overlay direct
      hasShownIntroOnLoginRef.current = true;
      setIsVisible(true);
      setPhase('intro');
      setHasGreeted(false);
      setHasPlayedIntro(false);
      setHasAutoListened(false);
    } else if (!user) {
      // Geen user - start met overlay gesloten
      setIsVisible(false);
      setPhase('idle');
    }
  }, []);

  // ============================================================================
  // LOAD AI USAGE INFO
  // ============================================================================

  useEffect(() => {
    const loadUsageInfo = async () => {
      if (!currentUser) return;
      
      try {
        const info = await aiUsageService.getAIUsageInfo(currentUser.uid);
        setUsageInfo(info);
      } catch (error) {
        console.error('Error loading AI usage info:', error);
      }
    };

    loadUsageInfo();
    
    // Reload every 30 seconds to keep it fresh
    const interval = setInterval(loadUsageInfo, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  // ============================================================================
  // INTRO END HANDLER
  // ============================================================================

  const handleIntroEnd = useCallback(() => {
    console.log('🎬 Intro video ended!');
    console.log('📊 State before greeting:', {
      skipGreeting: skipGreetingRef.current,
      hasCompletedIntro: hasCompletedIntroRef.current,
      userName
    });
    
    setHasPlayedIntro(true);
    setHasGreeted(true);
    hasCompletedIntroRef.current = true; // ✅ Onthoud dat intro compleet is (voorkomt dubbele welkomst)
    console.log('✅ hasCompletedIntroRef set to TRUE');
    
    // Check of we greeting moeten skippen (mini avatar click) - gebruik REF
    if (skipGreetingRef.current) {
      console.log('🎯 Skipping greeting (opened from mini avatar)');
      skipGreetingRef.current = false; // Reset voor volgende keer
      setTimeout(() => {
        setPhase('idle');
      }, 50);
    } else {
      // Direct naar speaking en start greeting
      console.log('🎯 Starting greeting in 50ms...');
      setTimeout(() => {
        console.log('🗣️ SPEAKING GREETING NOW: "Hi ' + (userName || '') + ', wat kan ik nu voor je doen?"');
        setPhase('speaking');
        speak(`Hi ${userName || ''}, wat kan ik nu voor je doen?`);
      }, 50);
    }
  }, [userName, speak]);
  
  // Debug: Log phase changes
  useEffect(() => {
    console.log('📊 Phase changed to:', phase);
  }, [phase]);

  // ============================================================================
  // AUTO-START MICROPHONE ONLY WHEN VISIBLE (SOFT MODE)
  // ============================================================================

  useEffect(() => {
    // ❌ NEVER start mic during intro or when muted
    if (phase === 'intro' || isMuted) {
      return;
    }
    
    if (isEnabled && isSupported && hasGreeted && hasPlayedIntro && !micStreamRef.current) {
      startMicAndAudioDetection();
    }
    if (!isEnabled) {
      cleanupMic();
    }
  }, [isEnabled, isSupported, hasGreeted, hasPlayedIntro, phase, isMuted, startMicAndAudioDetection, cleanupMic]);

  // ============================================================================
  // AUTO-LISTEN WHEN OVERLAY OPENS (SHOW "LUISTEREN" STATE)
  // ============================================================================
  useEffect(() => {
    // ❌ NEVER during intro or when muted
    if (phase === 'intro' || isMuted) {
      return;
    }
    
    // Only auto-start listening ONCE after greeting is done
    if (isVisible && isEnabled && isSupported && hasGreeted && hasPlayedIntro && !hasAutoListened && phase === 'idle') {
      console.log('🎧 Auto-listen triggered!');
      setHasAutoListened(true);
      transitionToListening();
    }
  }, [isVisible, isEnabled, isSupported, hasGreeted, hasPlayedIntro, hasAutoListened, phase, isMuted, transitionToListening]);

  // ============================================================================
  // BROWSER SUPPORT CHECK
  // ============================================================================

  useEffect(() => {
    if (!isSupported) {
      setBrowserWarning(true);
    }
  }, [isSupported]);

  // ============================================================================
  // UI HANDLERS
  // ============================================================================

  const handleCloseOverlay = useCallback(() => {
    setIsVisible(false);
    if (phase === 'speaking') {
      cleanupSpeech();
    }
    onClose?.();
  }, [phase, cleanupSpeech, onClose]);

  const toggleEnabled = useCallback((shouldSkipGreeting = false) => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    localStorage.setItem('ai_assistant_enabled', String(newEnabled));
    
    if (!newEnabled) {
      // AI uitzetten: cleanup en verberg overlay
      fullCleanup();
      setPhase('off');
      setIsVisible(false);
      skipGreetingRef.current = false; // Reset
    } else {
      // AI aanzetten: toon overlay
      setIsVisible(true);
      
      // ✅ FIX: Check of intro al compleet is
      if (hasCompletedIntroRef.current) {
        // Intro al gezien eerder - ga direct naar idle
        console.log('🎯 Toggle ON: Intro already completed, going to idle');
        setPhase('idle');
      } else {
        // Eerste keer - start met intro
        console.log('🎬 Toggle ON: Starting intro for first time');
        setPhase('intro');
        setHasGreeted(false);
        setHasPlayedIntro(false);
        setHasAutoListened(false);
        skipGreetingRef.current = shouldSkipGreeting;
      }
    }
  }, [isEnabled, fullCleanup]);

  // ============================================================================
  // MUTE TOGGLE - stops mic/speech but keeps avatar visible, no re-intro
  // ============================================================================

  const handleMuteToggle = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem('ai_assistant_muted', String(newMuted));
    
    if (newMuted) {
      // Mute: stop everything but keep enabled
      console.log('🔇 Muting AI Assistant');
      cleanupMic();
      cleanupSpeech();
      clearTranscript();
      clearIntent();
      isProcessingRef.current = false;
      setPhase('idle');
      setDisplayMessage('');
    } else {
      // Unmute: restart mic detection
      console.log('🔊 Unmuting AI Assistant');
      if (hasPlayedIntro && hasGreeted) {
        startMicAndAudioDetection();
      }
    }
  }, [isMuted, cleanupMic, cleanupSpeech, clearTranscript, clearIntent, hasPlayedIntro, hasGreeted, startMicAndAudioDetection]);

  // Block auto-listen and audio detection when muted
  useEffect(() => {
    if (isMuted && micStreamRef.current) {
      cleanupMic();
    }
  }, [isMuted, cleanupMic]);

  const handleMiniClick = useCallback(() => {
    console.log('🖱️ Mini avatar clicked! Current state:', {
      isEnabled,
      phase,
      hasCompletedIntro: hasCompletedIntroRef.current,
      hasGreeted,
      hasPlayedIntro,
      isVisible
    });
    
    // Mini avatar = open overlay (niet toggle)
    if (!isEnabled) {
      // Als AI uit staat, zet hem aan
      console.log('💡 AI is disabled, turning ON...');
      toggleEnabled(true);
    } else {
      // Als AI aan staat, open overlay direct
      console.log('📂 AI is enabled, opening overlay...');
      setIsVisible(true);
      
      // ✅ FIX: Check of intro al compleet is
      if (hasCompletedIntroRef.current) {
        // Intro al gezien - ga direct naar idle (geen dubbele welkomst)
        console.log('✅ Intro already completed, skipping greeting. Current phase:', phase);
        if (phase === 'off') {
          console.log('⚡ Phase was OFF, setting to idle');
          setPhase('idle');
        } else {
          console.log('⚡ Phase already', phase, '- keeping it');
        }
        // Als phase al idle/listening/etc is, laat het zo
      } else {
        // Nog nooit intro gezien - start met intro
        console.log('🎬 First time opening - starting intro');
        setPhase('intro');
        setHasGreeted(false);
        setHasPlayedIntro(false);
        setHasAutoListened(false);
      }
    }
  }, [toggleEnabled, isEnabled, phase, hasGreeted, hasPlayedIntro, isVisible]);

  // ============================================================================
  // RENDER - DISABLED STATE
  // ============================================================================

  if (!isEnabled) {
    return (
      <button
        onClick={() => toggleEnabled()}
        className="hidden md:flex fixed bottom-6 right-6 w-20 h-20 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full shadow-xl items-center justify-center text-white text-2xl hover:scale-110 transition-transform z-[9998] opacity-60 hover:opacity-100"
        aria-label="Activeer AI Assistant"
      >
        <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="6" fill="white" opacity="0.3" />
          <circle cx="16" cy="16" r="4" fill="white" opacity="0.6" />
          <circle cx="16" cy="9" r="1.5" fill="white" />
          <circle cx="19.5" cy="11.5" r="1.5" fill="white" />
          <circle cx="19.5" cy="20.5" r="1.5" fill="white" />
          <circle cx="16" cy="23" r="1.5" fill="white" />
          <circle cx="12.5" cy="20.5" r="1.5" fill="white" />
          <circle cx="12.5" cy="11.5" r="1.5" fill="white" />
          <circle cx="16" cy="16" r="2" fill="white" />
        </svg>
      </button>
    );
  }

  // ============================================================================
  // RENDER
  // Always render mini avatar; overlay conditionally
  // ============================================================================

  return (
    <>
      {/* Floating mini avatar - rechts onderin (alleen zichtbaar als overlay gesloten is); hidden op mobiel om overlap met BottomNavigation te voorkomen */}
      {!isVisible && (
        <div className="hidden md:block fixed bottom-6 right-6 z-[9998]">
          <div className="relative">
          {/* Avatar button */}
          <button
            onClick={handleMiniClick}
            className="cursor-pointer transition-transform hover:scale-110 active:scale-95"
            aria-label="Open AI Assistant"
          >
            <div
              className="relative inline-flex items-center justify-center rounded-full overflow-hidden"
              style={{
                padding: '6px',
                background: isMuted 
                  ? 'linear-gradient(135deg, rgba(156, 163, 175, 0.12), rgba(107, 114, 128, 0.12))'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(168, 85, 247, 0.08))',
                backdropFilter: 'blur(10px) saturate(150%)',
                WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                border: isMuted 
                  ? '1px solid rgba(156, 163, 175, 0.3)' 
                  : '1px solid rgba(99, 102, 241, 0.2)',
                boxShadow: isMuted
                  ? '0 4px 16px rgba(0, 0, 0, 0.08)'
                  : '0 8px 32px 0 rgba(99, 102, 241, 0.2), 0 0 18px rgba(99, 102, 241, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.6)',
                opacity: isMuted ? 0.6 : 1,
                transition: 'all 0.3s ease',
              }}
            >
              <div
                className="relative rounded-full overflow-hidden"
                style={{
                  width: '72px',
                  height: '72px',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
                  filter: isMuted ? 'grayscale(80%)' : 'none',
                  transition: 'filter 0.3s ease',
                }}
              >
                <StateLogo phase={phase} />
                <div
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35), inset 0 0 24px rgba(255,255,255,0.18)',
                  }}
                />
              </div>
            </div>
          </button>

          {/* Mute/Unmute toggle - rechts bovenop de avatar */}
          <button
            onClick={handleMuteToggle}
            className={`
              absolute -top-1 -right-1 z-[9999]
              w-7 h-7 rounded-full 
              shadow-lg hover:shadow-xl
              flex items-center justify-center 
              transition-all duration-200
              focus:outline-none focus:ring-2
              hover:scale-110 active:scale-95
              pointer-events-auto
              ${isMuted 
                ? 'bg-gray-100 hover:bg-green-50 border border-gray-300 hover:border-green-400 text-gray-500 hover:text-green-600 focus:ring-green-300' 
                : 'bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300 text-gray-600 hover:text-orange-500 focus:ring-orange-300'
              }
            `}
            aria-label={isMuted ? 'AI Assistant unmuten' : 'AI Assistant muten'}
            title={isMuted ? 'AI aanzetten (unmute)' : 'AI stil zetten (mute)'}
          >
            {isMuted ? (
              /* Unmute icon - speaker with line through */
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              /* Mute icon - speaker with waves */
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            )}
          </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {usageInfo && (
        <AIUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          charactersUsed={usageInfo.current}
          limit={usageInfo.limit}
          resetDate={usageInfo.resetDate}
        />
      )}

      {isVisible && (
        <div className="fixed inset-0 w-screen h-screen overflow-hidden z-[9999] pointer-events-auto" style={{ background: 'radial-gradient(ellipse at 60% 40%, #f5f0e8 0%, #fffbf3 45%, #eef5f2 100%)' }}>
          {/* Close button */}
          <button
            onClick={handleCloseOverlay}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-xl transition-colors z-10"
            style={{ background: 'rgba(139, 165, 155, 0.12)', color: 'rgba(80, 75, 70, 0.7)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(139, 165, 155, 0.22)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(139, 165, 155, 0.12)')}
            aria-label="Sluit AI Assistant"
          >
            ✕
          </button>

          <div className="h-full w-full flex flex-col">
            {/* Main content area - strak layout */}
            <div className="flex-1 flex items-center justify-center px-6 relative">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center max-w-6xl w-full">
                {/* Left title */}
                <div className="flex flex-col items-start space-y-3">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{ color: '#8BA59B' }}>Spraakassistent</span>
                  <h1 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: 'rgba(50, 45, 40, 0.92)' }}>
                    Hands-Free
                  </h1>
                  <p className="text-sm leading-relaxed max-w-sm" style={{ color: 'rgba(80, 75, 70, 0.65)' }}>
                    Netwerk, plan en bel zonder te klikken. Jouw stem is de snelste weg naar resultaat.
                  </p>
                </div>

                {/* Center state media (glassmorphism) */}
                <div className="flex justify-center">
                  <div
                    className="rounded-2xl overflow-hidden flex items-center justify-center relative"
                    style={{ 
                      width: 420, 
                      height: 260,
                      background: 'linear-gradient(135deg, rgba(139, 165, 155, 0.10), rgba(184, 196, 185, 0.10))',
                      backdropFilter: 'blur(10px) saturate(120%)',
                      WebkitBackdropFilter: 'blur(10px) saturate(120%)',
                      border: '1px solid rgba(139, 165, 155, 0.25)',
                      boxShadow: '0 8px 32px 0 rgba(139, 165, 155, 0.18), inset 0 1px 0 0 rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    {/* Warm filter wrapper */}
                    <div className="w-full h-full" style={{ filter: 'sepia(0.18) saturate(0.82) brightness(1.04) hue-rotate(5deg)' }}>
                      <StateLogo phase={phase} onIntroEnd={handleIntroEnd} />
                    </div>
                    {/* Subtle warm cream overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none rounded-2xl"
                      style={{ background: 'rgba(255, 251, 243, 0.10)', mixBlendMode: 'multiply' }}
                    />
                  </div>
                </div>

                {/* Right title */}
                <div className="flex flex-col items-start lg:items-end space-y-3 text-right lg:text-right">
                  <h1 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: 'rgba(50, 45, 40, 0.92)' }}>
                    Productief
                  </h1>
                  <p className="text-sm leading-relaxed max-w-sm text-left lg:text-right" style={{ color: 'rgba(80, 75, 70, 0.65)' }}>
                    Agenda, netwerken en bellen op commando. Jouw persoonlijke netwerkassistent.
                  </p>
                </div>
              </div>

              {/* Centered power toggle with label */}
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                <button
                  onClick={() => toggleEnabled()}
                  className="w-12 h-12 rounded-full text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform group"
                  style={{ background: '#8BA59B' }}
                  aria-label="AI Assistant uitzetten"
                  title="AI Assistant uitzetten"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="4" x2="12" y2="12" />
                    <path d="M7 6.5a8 8 0 1 0 10 0" />
                  </svg>
                </button>
                <span className="text-xs font-medium" style={{ color: '#8BA59B' }}>AI uitzetten</span>
              </div>
            </div>

            {/* Compact speech card rechts-onder in oranje huisstijl */}
            <div className="absolute bottom-6 right-6">
              <div className="rounded-xl text-white px-4 py-3 shadow-xl min-w-[200px] max-w-[260px]" style={{ background: '#8BA59B' }}>
                <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Spraak</div>
                <div className="text-sm leading-snug">
                  {interimTranscript || transcript || displayMessage || 'Ik luister...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistantOverlay;
